/**
 * Drawer — accessible slide-over panel rendered into document.body via React portal.
 *
 * Uses `.scrim show` (full-viewport dim from app-shell.css) and `.notif-drawer show`
 * (slide-in panel; the `.show` class is what flips the transform from
 * `translateX(100%)` to `translateX(0)`).
 *
 * The scrim is a sibling of the drawer (not a wrapper), matching the
 * design-system intent where the drawer slides in from the right.
 * Closes on scrim click or Escape keypress. The component mounts/unmounts
 * based on the `open` prop; the `.show` class is unconditionally applied
 * while open so the CSS transition reveals the panel.
 *
 * Usage:
 *   <Drawer open={isOpen} onClose={() => setOpen(false)} title="Notifications">
 *     <p>Drawer content here</p>
 *   </Drawer>
 */

import { type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/Icon";

/** Props for the {@link Drawer} component. */
export type DrawerProps = {
  /** Whether the drawer is visible. */
  open: boolean;
  /** Callback fired when the drawer should close (scrim click or Escape). */
  onClose: () => void;
  /** Accessible title shown in the drawer header. */
  title: string;
  /** Content rendered inside `.nd-body`. */
  children: ReactNode;
  /** Panel width in pixels. Defaults to 380. */
  width?: number;
};

/**
 * Drawer side-panel primitive.
 *
 * Renders null when `open` is false. When open, portals a scrim + drawer
 * pair into `document.body` and registers an Escape key listener.
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
  width = 380,
}: DrawerProps) {
  const panelRef = useRef<HTMLElement>(null);
  // See Modal: captured during the closed→open render so autofocusing
  // children can't make us "restore" focus into the removed panel.
  const triggerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  if (open && !wasOpenRef.current) {
    triggerRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
  }
  wasOpenRef.current = open;

  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Same focus contract as Modal: move focus into the panel on open,
  // restore the trigger on close (review finding).
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      panel.focus();
    }
    return () => triggerRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return createPortal(
    <>
      <div
        data-testid="drawer-scrim"
        className="scrim show"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        ref={panelRef}
        className="notif-drawer show"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{ width }}
      >
        <div className="nd-head">
          <h3 className="nd-title">{title}</h3>
          <button
            type="button"
            className="mx"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="close" />
          </button>
        </div>
        <div className="nd-body">{children}</div>
      </aside>
    </>,
    document.body,
  );
}
